import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Card } from 'react-bootstrap';
import { SalesEntryItem } from 'contexts/SalesEntryContext';
import { ProductSearchDropdown } from '../SearchableDropdown';
import { useProductSearch } from 'modules/transactions/new-sales-entry/services/use-sales-entry.service';

interface ItemModalProps {
  show: boolean;
  onHide: () => void;
  editingItem: string | null;
  items: SalesEntryItem[];
  onSubmit: (item: SalesEntryItem) => void;
  defaultSearchType?: 'bagNo' | 'styleNo' | 'igiNo' | 'huid';
}

interface FormErrors {
  [key: string]: string;
}

const CATEGORY_OPTIONS = [
  { value: 'Ring', label: 'Ring' },
  { value: 'Necklace', label: 'Necklace' },
  { value: 'Earrings', label: 'Earrings' },
  { value: 'Bracelet', label: 'Bracelet' },
  { value: 'Pendant', label: 'Pendant' }
];

const CLARITY_OPTIONS = [
  { value: 'FL', label: 'FL' },
  { value: 'IF', label: 'IF' },
  { value: 'VVS1', label: 'VVS1' },
  { value: 'VVS2', label: 'VVS2' },
  { value: 'VS1', label: 'VS1' },
  { value: 'VS2', label: 'VS2' }
];

const COLOR_OPTIONS = [
  { value: 'D', label: 'D' },
  { value: 'E', label: 'E' },
  { value: 'F', label: 'F' },
  { value: 'G', label: 'G' },
  { value: 'H', label: 'H' },
  { value: 'I', label: 'I' }
];

const KT_OPTIONS = [
  { value: 18, label: '18K' },
  { value: 22, label: '22K' },
  { value: 24, label: '24K' }
];

export const ItemModal: React.FC<ItemModalProps> = ({
  show,
  onHide,
  editingItem,
  items,
  onSubmit,
  defaultSearchType = 'styleNo'
}) => {
  const [formData, setFormData] = useState<Partial<SalesEntryItem>>({
    id: '',
    styleNo: '',
    category: '',
    diaGoldWt: 0,
    diaQty: 0,
    diaClarity: '',
    diaColor: '',
    netWt: 0,
    grossWt: 0,
    kt: '',
    karatType: '',
    pcs: 0,
    igiNo: '',
    huidNo: '',
    bagNo: '',
    mrp: 0,
    tag: '',
    isSNMCC: false,
    color: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [selectedProduct, setSelectedProduct] = useState<any>(null);

  // Use product search hook
  const {
    products,
    loading: productLoading,
    error: productError,
    searchProducts
  } = useProductSearch();

  // Initialize form data when modal opens
  useEffect(() => {
    if (show) {
      if (editingItem) {
        const item = items.find(i => i.id === editingItem);
        if (item) {
          setFormData(item);
        }
      } else {
        setFormData({
          id: Date.now().toString(),
          styleNo: '',
          category: '',
          diaGoldWt: 0,
          diaQty: 0,
          diaClarity: '',
          diaColor: '',
          netWt: 0,
          grossWt: 0,
          kt: '',
          karatType: '',
          pcs: 0,
          igiNo: '',
          huidNo: '',
          bagNo: '',
          mrp: 0,
          tag: '',
          isSNMCC: false,
          color: '',
        });
      }
      setErrors({});
    }
  }, [show, editingItem, items]);

  const updateField = (field: keyof SalesEntryItem, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.styleNo?.trim()) {
      newErrors.styleNo = 'Style No. is required';
    }

    if (!formData.category?.trim()) {
      newErrors.category = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData as SalesEntryItem);
      onHide();
    }
  };

  const handleClose = () => {
    setFormData({});
    setErrors({});
    setSelectedProduct(null);
    onHide();
  };

  // Handle product selection from search dropdown
  const handleProductSelect = (product: any) => {
    setSelectedProduct(product);
    if (product) {
      setFormData(prev => ({
        ...prev,
        styleNo: product.styleNo,
        category: product.category,
        diaGoldWt: product.diaGoldWt || 0,
        diaQty: product.diaQty || 0,
        diaClarity: product.diaClarity || '',
        diaColor: product.diaColor || '',
        netWt: product.netWt || 0,
        grossWt: product.grossWt || 0,
        kt: product.karatType || '',
        karatType: product.karatType || '',
        pcs: product.pcs || 1,
        igiNo: product.igiNo || '',
        huidNo: product.huidNo || '',
        bagNo: product.bagNo || '',
        mrp: product.mrpPrice || 0,
        tag: product.tag || '',
        isSNMCC: product.isSNMCC || false,
        color: product.color || '',
      }));
    }
  };

  const renderFormField = (
    label: string,
    field: keyof SalesEntryItem,
    type: 'text' | 'number' | 'select' | 'checkbox' = 'text',
    options?: { value: any; label: string }[],
    required: boolean = false,
    step?: string
  ) => {
    return (
      <Form.Group>
        <Form.Label>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
        {type === 'select' ? (
          <Form.Select
            value={formData[field] as string || ''}
            onChange={(e) => updateField(field, e.target.value)}
            isInvalid={!!errors[field]}
          >
            <option value="">Select {label}</option>
            {options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        ) : type === 'checkbox' ? (
          <Form.Check
            type="checkbox"
            label={label}
            className="mt-2"
            checked={(formData[field] as boolean) || false}
            onChange={(e) => updateField(field, e.target.checked)}
            isInvalid={!!errors[field]}
          />
        ) : (
          <Form.Control
            type={type}
            placeholder={type === 'text' ? `Enter ${label.toLowerCase()}` : undefined}
            value={formData[field] as string | number || (type === 'number' ? 0 : '')}
            onChange={(e) => {
              const value = type === 'number'
                ? (field === 'kt' || field === 'diaQty' || field === 'pcs')
                  ? parseInt(e.target.value) || 0
                  : parseFloat(e.target.value) || 0
                : e.target.value;
              updateField(field, value);
            }}
            isInvalid={!!errors[field]}
            {...(step && { step })}
          />
        )}
        {errors[field] && (
          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
            {errors[field]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{editingItem ? 'Edit Item' : 'Add New Item'}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Product Search Section */}
          <Row className="mb-3">
            <Col md={12}>
              <ProductSearchDropdown
                value={selectedProduct}
                onChange={handleProductSelect}
                onSearch={searchProducts}
                options={products}
                loading={productLoading}
                error={productError}
                label="Search & Select Product"
                placeholder="Search by Style No, Bag No, IGI, or HUID..."
                searchType={defaultSearchType}
              />
            </Col>
          </Row>

          {selectedProduct && (
            <Card className="bg-light mb-3">
              <Card.Body className="p-2">
                <small className="text-muted">Selected Product:</small>
                <div className="fw-medium">{selectedProduct.styleNo} - {selectedProduct.category}</div>
                <div className="d-flex justify-content-between">
                  <small>Weight: {selectedProduct.netWt}g ({selectedProduct.kt}K)</small>
                  <small className="text-success fw-bold">₹{selectedProduct.mrpPrice?.toLocaleString()}</small>
                </div>
              </Card.Body>
            </Card>
          )}

          <div className="text-center my-3">
            <span className="text-muted">or enter details manually</span>
          </div>

          {/* Row 1: Style No & Category */}
          <Row className="mb-3">
            <Col md={6}>
              {renderFormField('Style No.', 'styleNo', 'text', undefined, true)}
            </Col>
            <Col md={6}>
              {renderFormField('Category', 'category', 'select', CATEGORY_OPTIONS, true)}
            </Col>
          </Row>

          {/* Row 2: Diamond/Gold Details */}
          <Row className="mb-3">
            <Col md={4}>
              {renderFormField('Dia/Gold Wt', 'diaGoldWt', 'number', undefined, false, '0.01')}
            </Col>
            <Col md={4}>
              {renderFormField('Dia Qty', 'diaQty', 'number')}
            </Col>
            <Col md={4}>
              {renderFormField('Dia Clarity', 'diaClarity', 'select', CLARITY_OPTIONS)}
            </Col>
          </Row>

          {/* Row 3: Color & Weights */}
          <Row className="mb-3">
            <Col md={4}>
              {renderFormField('Dia Color', 'diaColor', 'select', COLOR_OPTIONS)}
            </Col>
            <Col md={4}>
              {renderFormField('Net Wt', 'netWt', 'number', undefined, false, '0.01')}
            </Col>
            <Col md={4}>
              {renderFormField('Gross Wt', 'grossWt', 'number', undefined, false, '0.01')}
            </Col>
          </Row>

          {/* Row 4: Karat & Pieces */}
          <Row className="mb-3">
            <Col md={3}>
              {renderFormField('Kt', 'kt', 'select', KT_OPTIONS)}
            </Col>
            <Col md={3}>
              {renderFormField('PCS', 'pcs', 'number')}
            </Col>
            <Col md={3}>
              {renderFormField('IGI No', 'igiNo', 'text')}
            </Col>
            <Col md={3}>
              {renderFormField('HUID No', 'huidNo', 'text')}
            </Col>
          </Row>

          {/* Row 5: Bag, MRP, Tag */}
          <Row className="mb-3">
            <Col md={4}>
              {renderFormField('Bag No', 'bagNo', 'text')}
            </Col>
            <Col md={4}>
              {renderFormField('MRP', 'mrp', 'number', undefined, false, '0.01')}
            </Col>
            <Col md={4}>
              {renderFormField('Tag', 'tag', 'text')}
            </Col>
          </Row>

          {/* Row 6: Color & SNMCC */}
          <Row className="mb-3">
            <Col md={6}>
              {renderFormField('Color', 'color', 'text')}
            </Col>
            <Col md={6} className="d-flex align-items-end">
              {renderFormField('Is SNMCC', 'isSNMCC', 'checkbox')}
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {editingItem ? 'Update Item' : 'Add Item'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};